import React from "react";
import Navbar from "../../component/Navbar/Navbar";
import Footer from "../../component/Footer/Footer";
import { Link } from "react-router-dom";
import {
  ArrowLongRightIcon,
  ShieldCheckIcon,
  DocumentChartBarIcon,
  CloudArrowUpIcon,
  WrenchScrewdriverIcon,
  SparklesIcon,
  CircleStackIcon,
  CodeBracketIcon,
  CpuChipIcon,
} from "@heroicons/react/24/outline";

const features = [
  {
    icon: SparklesIcon,
    title: "AI-Powered Intelligence",
    description: "Smart categorisation, anomaly detection, and predictive cash flow analysis powered by machine learning.",
  },
  {
    icon: CodeBracketIcon,
    title: "Developer-Friendly API",
    description: "RESTful API with comprehensive documentation. Integrate Auditra's accounting engine into your existing software stack.",
  },
  {
    icon: DocumentChartBarIcon,
    title: "Real-Time Reporting",
    description: "Profit & loss, balance sheets, and cash flow statements generated instantly, with no waiting for month-end closes.",
  },
  {
    icon: CloudArrowUpIcon,
    title: "Cloud-Native & Paperless",
    description: "Digitise receipts, invoices, and records. Access everything from anywhere, on any device.",
  },
  {
    icon: ShieldCheckIcon,
    title: "Compliance Ready",
    description: "Built-in tax calculations, audit trails, and regulatory reporting tailored to Nigerian business requirements.",
  },
  {
    icon: WrenchScrewdriverIcon,
    title: "Plug-and-Play Integration",
    description: "Connect with banks, payment processors, and existing ERPs. Auditra fits into your workflow, not the other way around.",
  },
];

const AuditraPage = () => {
  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center bg-white overflow-hidden" id="auditra">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
        <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-emerald-100 rounded-full blur-3xl opacity-60" />
        <div className="absolute bottom-20 right-1/4 w-[400px] h-[400px] bg-emerald-50 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-16 w-full pt-32 pb-20 lg:pt-40 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full mb-8 border border-emerald-100">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="text-sm font-medium text-emerald-700">Coming Soon</span>
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-[1.1] tracking-tight">
                Auditra
              </h1>
              <p className="mt-2 text-xl text-emerald-600 font-medium">AI-powered accounting for the modern business</p>

              <p className="mt-8 text-lg text-gray-500 leading-relaxed max-w-lg">
                Auditra is an accounting management solution built to move businesses
                from the paper age to a paperless, AI-driven future. Designed for companies
                that need real-time financial visibility, automated workflows, and smart
                reporting, without the complexity of legacy accounting software.
              </p>

              <div className="mt-8 space-y-4">
                {[
                  "AI-driven bookkeeping and expense categorisation",
                  "RESTful API for seamless integration with existing software",
                  "Real-time financial dashboards and reporting",
                  "Multi-business and multi-currency support",
                  "Invoice generation, tracking, and payment automation",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="mt-1 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-600">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/25 hover:-translate-y-0.5 group"
                >
                  Join the Waitlist
                  <ArrowLongRightIcon className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold text-gray-700 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 hover:border-gray-300 transition-all duration-300"
                >
                  API Documentation (Soon)
                </Link>
              </div>
            </div>

            {/* Auditra dashboard mockup */}
            <div className="relative">
              <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-6 shadow-2xl border border-white/10">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                  <span className="ml-2 text-xs text-gray-500">auditra.devdesk.ng</span>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between bg-white/5 rounded-xl p-4">
                    <div>
                      <div className="text-xs text-gray-500">Total Revenue (Q1)</div>
                      <div className="text-2xl font-bold text-white mt-1">₦48,230,000</div>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-emerald-400 font-medium">
                      <SparklesIcon className="w-4 h-4" />
                      AI Insights
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white/5 rounded-xl p-3 text-center">
                      <div className="text-xs text-gray-500">Invoices</div>
                      <div className="text-lg font-bold text-white">127</div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-3 text-center">
                      <div className="text-xs text-gray-500">Expenses</div>
                      <div className="text-lg font-bold text-white">89</div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-3 text-center">
                      <div className="text-xs text-gray-500">Profit</div>
                      <div className="text-lg font-bold text-emerald-400">32%</div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4">
                    <div className="text-xs text-gray-500 mb-3">Monthly Cash Flow</div>
                    <div className="flex items-end gap-2 h-20">
                      {[50, 70, 45, 80, 60, 90, 75, 85, 65, 95, 80, 88].map((h, i) => (
                        <div key={i} className="flex-1 rounded-t-md bg-gradient-to-t from-emerald-600 to-emerald-400" style={{ height: `${h}%` }} />
                      ))}
                    </div>
                    <div className="flex justify-between mt-2 text-[10px] text-gray-600">
                      <span>Jan</span><span>Mar</span><span>May</span><span>Jul</span><span>Sep</span><span>Nov</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 bg-emerald-500/10 rounded-xl p-3 border border-emerald-500/20">
                    <SparklesIcon className="w-5 h-5 text-emerald-400" />
                    <div className="text-sm text-gray-300">
                      AI detected 3 duplicate expenses. <span className="text-emerald-400">Review now</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl px-5 py-3 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <CodeBracketIcon className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">API-First</div>
                    <div className="text-xs text-gray-500">Plug into any system</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 lg:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Why businesses will choose Auditra
            </h2>
            <p className="text-lg text-gray-500">
              Built for African businesses, scalable for global operations. A fresh take on accounting software.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mb-6 group-hover:bg-emerald-600 transition-colors duration-300">
                  <feature.icon className="w-6 h-6 text-emerald-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-500 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* API Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Code snippet */}
            <div className="relative bg-gray-900 rounded-2xl p-6 lg:p-8 font-mono text-sm overflow-hidden border border-gray-800">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
                <span className="ml-2 text-xs text-gray-500 font-sans">api-example.js</span>
              </div>
              <pre className="text-gray-300 leading-relaxed overflow-x-auto">
                <code>{`// Create an invoice with Auditra API
const response = await fetch(
  'https://api.auditra.ng/v1/invoices',
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer YOUR_API_KEY',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      client: 'Acme Corp',
      items: [
        { description: 'Consulting',
          amount: 500000 }
      ],
      currency: 'NGN',
      due_date: '2026-03-15'
    })
  }
);

// AI auto-categorises the transaction
const invoice = await response.json();
// { id: "inv_abc123", status: "sent" }`}</code>
              </pre>
            </div>

            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full mb-6 border border-emerald-100">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">API</span>
              </div>

              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Built for developers
              </h2>

              <p className="text-lg text-gray-500 leading-relaxed mb-8">
                Auditra's API lets you embed accounting capabilities directly into
                your existing applications. Create invoices, track expenses, generate
                reports, and let AI handle the categorisation, all programmatically.
              </p>

              <div className="space-y-4">
                {[
                  "RESTful API with JSON responses",
                  "Webhook notifications for real-time events",
                  "SDKs for JavaScript, Python, and PHP",
                  "Sandbox environment for testing",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CircleStackIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-gray-600">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-7 py-3.5 text-base font-semibold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/25 hover:-translate-y-0.5 group"
                >
                  Get Early API Access
                  <ArrowLongRightIcon className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pool cross-sell */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 bg-white rounded-2xl p-8 border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <CpuChipIcon className="w-6 h-6 text-[#0186ef]" />
              </div>
              <div>
                <div className="text-lg font-bold text-gray-900">Also from DevDesk: Devdesk Pool</div>
                <div className="text-sm text-gray-500">The SaaS platform powering a growing network of betting operators nationwide.</div>
              </div>
            </div>
            <Link
              to="/products/pool"
              className="inline-flex items-center px-6 py-3 text-sm font-semibold text-[#0186ef] bg-blue-50 rounded-xl hover:bg-blue-100 transition-all duration-300 group flex-shrink-0"
            >
              Explore Devdesk Pool
              <ArrowLongRightIcon className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default AuditraPage;
